/**
 * Adaptation of the original Placeholder component:
 *
 * - https://github.com/Sitecore/jss/blob/v21.0.1/packages/sitecore-jss-react/src/components/Placeholder.tsx
 * - https://github.com/Sitecore/jss/blob/v21.0.1/packages/sitecore-jss-react/src/components/PlaceholderCommon.tsx
 * - https://docs.acc.eneco.nl/docs/decisions/sitecore-jss/#jss-react--nextjs-sdks
 */

import { PropsWithChildren, FC, createElement, createContext, useContext, useMemo, useRef } from 'react';

import { delve } from '@eneco/common/delve';
import env from '@eneco/common/env';
import logger from '@eneco/common/log';
import { NbaFeedbackWrapperComponent } from '@eneco/next-best-action';
import { TrackingProvider } from '@eneco/tracking';
import type {
  ComponentRendering,
  PlaceholderData,
  SitecoreComponent,
  ComponentProps,
  PlaceholderProps,
  UseContent,
} from '@sitecore/types/lib';
import { LayoutServiceData, RouteData } from '@sitecore/types/lib';
import type { HtmlElementRendering } from '@sitecore-jss/sitecore-jss-nextjs';

import { usePlaceholder } from '..';
import { useComponentFactory, useLayoutData } from './SitecoreContext';
import type { ComponentFactory } from './SitecoreContext';
import WithErrorLogging from './WithErrorLogging';
import { HiddenRendering } from '../components/HiddenRendering';

// Singleton: the vanilla-extract Vite plugin can cause this module to be evaluated twice in the
// same process (separate SSR module graph), creating two distinct React contexts and breaking the
// provider/consumer relationship. Storing on globalThis ensures a single shared instance.
const RENDERING_CONTEXT_KEY = '__sitecore_rendering_context__' as const;
type RenderingContextType = ReturnType<typeof createContext<ComponentRendering | undefined>>;

const RenderingContext: RenderingContextType =
  (globalThis as unknown as Record<string, RenderingContextType>)[RENDERING_CONTEXT_KEY] ??
  createContext<ComponentRendering | undefined>(undefined);

(globalThis as unknown as Record<string, RenderingContextType>)[RENDERING_CONTEXT_KEY] = RenderingContext;

// --- Proxy Logic for Missing Fields ---
const ENABLE_CMS_MISSING_LABELS =
  env('ENABLE_CMS_MISSING_LABELS') === '1' || env('FE_ENABLE_CMS_MISSING_LABELS') === '1';

type PathValue<T> = T extends object ? { [K in keyof T]: PathValue<T[K]> } : string;

function readScField<T extends object>(obj: T, basePath = '', defaultValues?: Record<string, string>): T {
  if (!ENABLE_CMS_MISSING_LABELS) return obj;
  return new Proxy(obj, {
    get(target, prop) {
      const path = basePath ? `${basePath}.${String(prop)}` : String(prop);
      const value = target[prop as keyof T];

      const hasDefaultValue = !!defaultValues?.[path];

      if (value === undefined || value === null) {
        return hasDefaultValue
          ? `${defaultValues?.[path]} [Missing in CMS: fields.${path}]`.trim()
          : `[Missing in CMS: fields.${path}]`;
      }
      if (typeof value === 'string' && value.toString().trim() === '') {
        return hasDefaultValue
          ? `${defaultValues?.[path]} [Empty field in CMS: fields.${path}]`.trim()
          : `[Empty field in CMS: fields.${path}]`.trim();
      }

      if (typeof value === 'object') {
        return readScField(value, path, defaultValues);
      }

      return value;
    },
  });
}

const getContentGetter =
  <T extends ComponentRendering>(rendering: T) =>
  (path: string) => {
    const value = delve(rendering.fields ?? {}, path);
    if (value) return value;
    logger.error('D6jsNU', 'Content not found in rendering', { path, rendering });
    return `[${path}]`;
  };

const EMPTY_OBJECT = {};

// Memoizes a record to prevent infinite re-renders when callers pass an inline object
function useStableRecord<T extends Record<string, string>>(record: T): T {
  const ref = useRef(record);

  const isChanged =
    Object.keys(ref.current).length !== Object.keys(record).length ||
    Object.keys(record).some(k => ref.current[k] !== record[k]);

  if (isChanged) {
    ref.current = record;
  }

  return ref.current;
}

export const useContent = <T extends ComponentRendering>(
  ancestorRendering?: RouteData | ComponentRendering,
  defaultValues: Record<string, string> = EMPTY_OBJECT,
): UseContent<T, PathValue<T['fields']>> => {
  const currentRendering = useContext(RenderingContext);
  const rendering = (currentRendering ?? ancestorRendering) as T;

  // Use a stable reference for defaultValues to ensure proxiedFields is properly memoized
  const stableDefaultValues = useStableRecord(defaultValues);

  const f = useMemo(() => getContentGetter<T>(rendering), [rendering]);

  const proxiedFields = useMemo(() => {
    return readScField(rendering.fields ?? {}, '', stableDefaultValues) as PathValue<T['fields']>;
  }, [stableDefaultValues, rendering.fields]);

  return {
    ...rendering,
    fields: proxiedFields,
    f,
  };
};

export const RenderingProvider: FC<PropsWithChildren<{ rendering: ComponentRendering }>> = ({
  children,
  rendering,
}) => <RenderingContext.Provider value={rendering}>{children}</RenderingContext.Provider>;

const getPlaceholderDataFromRendering = (name: string, rendering: RouteData | ComponentRendering): PlaceholderData => {
  if (rendering?.placeholders && name in rendering.placeholders) {
    return rendering.placeholders[name] as PlaceholderData;
  } else {
    return [];
  }
};

// This should probably exclude the TrackingProvider for the EE as well, but for now it's just the NBAFeedbackWrapperComponent
const addComponentTracking = (component: SitecoreComponent, context: LayoutServiceData['sitecore']['context']) => {
  // If Sitecore is in edit or preview mode, don't wrap the component with tracking
  if (context.pageState !== 'normal') return component;

  const { params: { nba_feedback = '' } = {} } = component.props;
  return <NbaFeedbackWrapperComponent nbaFeedback={String(nba_feedback)}>{component}</NbaFeedbackWrapperComponent>;
};

export const wrapComponent = (component: SitecoreComponent, context: LayoutServiceData['sitecore']['context']) => {
  const { componentName } = component.props;

  // Don't wrap HTML element renderings
  if (!componentName) return component;

  return (
    <TrackingProvider key={component.key} scope={componentName}>
      <RenderingProvider rendering={component.props}>
        <WithErrorLogging componentName={componentName}>{addComponentTracking(component, context)}</WithErrorLogging>
      </RenderingProvider>
    </TrackingProvider>
  );
};

const isSitecoreExperienceEditorRendering = (
  rendering: HtmlElementRendering | ComponentRendering,
): rendering is HtmlElementRendering => {
  return !(rendering as ComponentRendering).componentName && (rendering as HtmlElementRendering).name !== undefined;
};

export const isSitecoreComponent = (component: unknown): component is SitecoreComponent => !!component;

export const getComponentsForRendering = (
  placeholderData: PlaceholderData,
  componentFactory: ComponentFactory,
  context: LayoutServiceData['sitecore']['context'],
) => {
  return (placeholderData ?? [])
    .map((rendering, index) => {
      const key = (rendering as ComponentRendering).uid ?? `component-${index}`;

      // HTML element rendering for Experience Editor
      if (isSitecoreExperienceEditorRendering(rendering)) {
        const { attributes } = rendering;
        if ((rendering as HtmlElementRendering).name === 'div') {
          return HiddenRendering();
        }
        const props = attributes;
        if (!props.className && attributes.class) props.className = attributes.class;
        if (attributes.chrometype === 'placeholder') {
          props.phkey = attributes.key;
        }
        delete props.class;

        return createElement(rendering.name, {
          // Explicitly NOT setting `key` here (breaks (dynamic) placeholders inside the EE)
          ...props,
          // Explicitly not parsing or sanitizing this HTML (extra metadata for editor)
          dangerouslySetInnerHTML: {
            __html: rendering.contents,
          },
        });
      }

      if (rendering.datasourceRequired && !rendering.fields) {
        return null;
      }

      const component = componentFactory(rendering.componentName);
      if (!component) {
        logger.error('Lwaar8', 'Component not found by factory', rendering);
        return null;
      }
      const props: ComponentProps = { ...rendering, context };
      return createElement(component, { key, ...props });
    })
    .filter(isSitecoreComponent)
    .map(component => wrapComponent(component, context));
};

export const usePlaceholderComponentsForRendering = (name: string) => {
  const componentFactory = useComponentFactory();
  const { context, route } = useLayoutData();
  const ensuredRendering = useContent(route);
  const { getScope, getScopedComponents } = usePlaceholder();
  const placeholderData = getPlaceholderDataFromRendering(name, ensuredRendering);
  const scopedComponentData = getScope(name) ? getScopedComponents(name) : null;
  const componentData = scopedComponentData || placeholderData;
  const components = getComponentsForRendering(componentData, componentFactory, context);
  return components;
};

export const Placeholder: FC<PlaceholderProps> = ({ name }) => {
  const components = usePlaceholderComponentsForRendering(name);
  return <>{components}</>;
};
